import React, { useRef, useState, useEffect, useCallback } from "react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_IMAGES = {
  1: "https://static.prod-images.emergentagent.com/jobs/a81ad897-661a-406c-a959-8074efa7a22b/images/ffcb4c06ab9943164a908b066ff8696a87ad84a2ce77cf8f8ee7757f0278f40e.png",
  2: "https://static.prod-images.emergentagent.com/jobs/a81ad897-661a-406c-a959-8074efa7a22b/images/829963673f982b34f9c6d30aa7fc0a104eef5c38471e1e97cd339b813d1b01c1.png",
  3: "https://static.prod-images.emergentagent.com/jobs/a81ad897-661a-406c-a959-8074efa7a22b/images/9b63bd42a3f44b6b1555cb2658f9d45706942f74225d481f6da7675ca66d2c62.png",
  4: "https://static.prod-images.emergentagent.com/jobs/a81ad897-661a-406c-a959-8074efa7a22b/images/5119c505cc2f437ee3b7fc31257118297eadbcc50502935686665cdbbd57cb70.png",
  5: "https://static.prod-images.emergentagent.com/jobs/a81ad897-661a-406c-a959-8074efa7a22b/images/79822e02b89dc49eeee29de1cc9c392836a5108f274ef81c46ac1a0a99db8988.png",
};

const TESTIMONIAL_SCENES = {
  1: {
    name: "Sarah Mitchell",
    role: "Professional Actor, London",
    duration: null,
    keyPhrases: [
      { text: "I was sceptical.", delay: 2 },
      { text: "Something was\nalways missing.", delay: 8 },
      { text: "It wasn't about\nwhat I was doing.", delay: 16 },
      { text: "It was about how\nI was being.", delay: 20 },
      { text: "I stopped performing\nand started arriving.", delay: 28 },
      { text: "That shift changed\neverything.", delay: 34 },
    ],
  },
  2: {
    name: "Marcus Thompson",
    role: "Theatre Director",
    duration: null,
    keyPhrases: [
      { text: "I've directed over\nforty productions.", delay: 1 },
      { text: "I can spot technique\na mile away.", delay: 5 },
      { text: "What I can't teach\nis presence.", delay: 9 },
      { text: "Mel Masry\ncan teach it.", delay: 14 },
      { text: "They came back\ndifferent.", delay: 20 },
      { text: "The room changes\nwhen they walk in.", delay: 26 },
    ],
  },
  3: {
    name: "Amara Okafor",
    role: "Actor & Movement Coach",
    duration: null,
    keyPhrases: [
      { text: "I thought I understood\nthe body-mind connection.", delay: 1 },
      { text: "GPS showed me\nhow much deeper\nit goes.", delay: 6 },
      { text: "An internal architecture\nthat holds you.", delay: 13 },
      { text: "It changed how\nI perform.", delay: 20 },
      { text: "It changed how\nI teach.", delay: 24 },
      { text: "It's that\nfoundational.", delay: 30 },
    ],
  },
  4: {
    name: "James Chen",
    role: "Screen Actor, 12 Years",
    duration: null,
    keyPhrases: [
      { text: "I nearly\nquit acting.", delay: 1 },
      { text: "Something wasn't\nlanding.", delay: 7 },
      { text: "Stop trying to\nbe interesting.", delay: 14 },
      { text: "Be interested.", delay: 18 },
      { text: "I booked my\nfirst lead role.", delay: 24 },
      { text: "The camera sees\neverything. And now,\nit sees me.", delay: 30 },
    ],
  },
  5: {
    name: "Elena Rossi",
    role: "GPS Method Graduate",
    duration: null,
    keyPhrases: [
      { text: "I came feeling\ncompletely lost.", delay: 1 },
      { text: "All these techniques\nbut no foundation.", delay: 5 },
      { text: "Grounding taught me\nto trust my body.", delay: 10 },
      { text: "Presence taught me\nto trust the moment.", delay: 14 },
      { text: "Structure taught me\nto trust the process.", delay: 18 },
      { text: "That confidence\nisn't performed.\nIt's real.", delay: 26 },
    ],
  },
};

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

function TestimonialReel({ id, testimonial }) {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const animRef = useRef(null);
  const startTimeRef = useRef(null);
  const avatarRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [recording, setRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const scenes = TESTIMONIAL_SCENES[id];
  const durationRef = useRef(40);

  // Preload avatar image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = AVATAR_IMAGES[id];
    img.onload = () => {
      avatarRef.current = img;
      drawFrame(0);
    };
  }, [id]);

  // Preload audio
  const loadAudio = useCallback(async () => {
    if (audioRef.current) return;
    setLoading(true);
    setError(null);
    try {
      const audio = new Audio(`${API_URL}/api/tts/testimonial/${id}`);
      audio.crossOrigin = "anonymous";
      await new Promise((resolve, reject) => {
        audio.oncanplaythrough = resolve;
        audio.onerror = reject;
        audio.load();
      });
      audioRef.current = audio;
      durationRef.current = audio.duration + 3;
      setAudioLoaded(true);
    } catch (e) {
      setError("Failed to load audio");
      console.error(e);
    }
    setLoading(false);
  }, [id]);

  const drawFrame = useCallback(
    (time) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      const W = 1080;
      const H = 1920;
      const totalDuration = durationRef.current;

      // Background
      ctx.fillStyle = "#1a1c1b";
      ctx.fillRect(0, 0, W, H);

      // Subtle radial gradient behind avatar
      const grad = ctx.createRadialGradient(W / 2, H * 0.28, 0, W / 2, H * 0.28, 400);
      grad.addColorStop(0, "rgba(217, 251, 6, 0.04)");
      grad.addColorStop(1, "transparent");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // Top accent line
      ctx.fillStyle = "#d9fb06";
      ctx.fillRect(0, 0, W, 4);

      // Header label
      const headerAlpha = time < 1.5 ? easeOutCubic(time / 1.5) : time > totalDuration - 2 ? Math.max(0, (totalDuration - time) / 2) : 1;
      ctx.globalAlpha = headerAlpha;
      ctx.font = "300 20px Inter, Arial, sans-serif";
      ctx.fillStyle = "#888680";
      ctx.textAlign = "center";
      ctx.letterSpacing = "4px";
      ctx.fillText("GPS METHOD™ TESTIMONIAL", W / 2, 80);
      ctx.letterSpacing = "0px";
      ctx.globalAlpha = 1;

      // === AVATAR PORTRAIT ===
      const avatarImg = avatarRef.current;
      if (avatarImg) {
        const avatarSize = 280;
        const avatarX = W / 2;
        const avatarY = H * 0.22;
        const avatarFade = time < 1.5 ? easeOutCubic(time / 1.5) : time > totalDuration - 2 ? Math.max(0, (totalDuration - time) / 2) : 1;
        const avatarScale = 0.92 + easeOutCubic(Math.min(time / 2, 1)) * 0.08;

        // Pulsing glow ring when audio playing
        const glowSize = avatarSize / 2 + 12;
        const pulseAmount = (playing || recording) ? 4 + Math.sin(time * 4) * 4 : 0;
        ctx.globalAlpha = avatarFade * 0.4;
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, glowSize + pulseAmount, 0, Math.PI * 2);
        ctx.strokeStyle = "#d9fb06";
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Outer ring
        ctx.globalAlpha = avatarFade * 0.2;
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, avatarSize / 2 + 6, 0, Math.PI * 2);
        ctx.strokeStyle = "#d9fb06";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.globalAlpha = 1;

        // Clip and draw avatar as circle
        ctx.save();
        ctx.globalAlpha = avatarFade;
        ctx.translate(avatarX, avatarY);
        ctx.scale(avatarScale, avatarScale);
        ctx.beginPath();
        ctx.arc(0, 0, avatarSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(avatarImg, -avatarSize / 2, -avatarSize / 2, avatarSize, avatarSize);
        ctx.restore();

        // Inner ring on top
        ctx.globalAlpha = avatarFade * 0.6;
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, avatarSize / 2 * avatarScale, 0, Math.PI * 2);
        ctx.strokeStyle = "#d9fb06";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.globalAlpha = 1;
      }

      // Name and role (below avatar)
      const nameAlpha = time < 2 ? easeOutCubic(time / 2) : time > totalDuration - 2 ? Math.max(0, (totalDuration - time) / 2) : 1;
      ctx.globalAlpha = nameAlpha;
      ctx.font = "700 40px Inter, Arial, sans-serif";
      ctx.fillStyle = "#d9fb06";
      ctx.textAlign = "center";
      ctx.fillText(scenes.name.toUpperCase(), W / 2, H * 0.37);

      ctx.font = "300 26px Inter, Arial, sans-serif";
      ctx.fillStyle = "#888680";
      ctx.fillText(scenes.role, W / 2, H * 0.4);

      // Divider
      ctx.fillStyle = "#d9fb06";
      ctx.fillRect(W / 2 - 30, H * 0.42, 60, 2);
      ctx.globalAlpha = 1;

      // Waveform visualizer
      const barCount = 50;
      const barWidth = 6;
      const barGap = 5;
      const totalBarWidth = barCount * (barWidth + barGap);
      const startX = (W - totalBarWidth) / 2;
      const centerY = H * 0.48;

      for (let i = 0; i < barCount; i++) {
        const phase = time * 3.5 + i * 0.35;
        const amplitude = playing || recording ? 12 + Math.sin(phase) * 10 + Math.sin(phase * 2.1) * 6 + Math.cos(phase * 0.7) * 8 : 3;
        const x = startX + i * (barWidth + barGap);
        const barAlpha = time < 2 ? easeOutCubic(time / 2) : time > totalDuration - 2 ? Math.max(0, (totalDuration - time) / 2) : 1;
        ctx.globalAlpha = barAlpha * 0.5;
        ctx.fillStyle = "#d9fb06";
        ctx.fillRect(x, centerY - amplitude, barWidth, amplitude * 2);
      }
      ctx.globalAlpha = 1;

      // Key phrases (below waveform)
      const phraseY = H * 0.58;
      let activePhrase = null;
      let nextPhraseTime = Infinity;

      for (let i = 0; i < scenes.keyPhrases.length; i++) {
        const phrase = scenes.keyPhrases[i];
        const next = scenes.keyPhrases[i + 1];
        if (time >= phrase.delay && (!next || time < next.delay)) {
          activePhrase = phrase;
          nextPhraseTime = next ? next.delay : totalDuration;
          break;
        }
      }

      if (activePhrase) {
        const phraseElapsed = time - activePhrase.delay;
        const phraseDuration = nextPhraseTime - activePhrase.delay;
        const fadeIn = Math.min(phraseElapsed / 0.6, 1);
        const fadeOut = phraseDuration - phraseElapsed < 0.5 ? (phraseDuration - phraseElapsed) * 2 : 1;
        const alpha = easeOutCubic(fadeIn) * Math.max(0, fadeOut);
        const slideUp = (1 - easeOutCubic(fadeIn)) * 30;

        // Quote marks
        ctx.globalAlpha = alpha * 0.15;
        ctx.font = 'italic 400 200px Georgia, serif';
        ctx.fillStyle = "#d9fb06";
        ctx.textAlign = "left";
        ctx.fillText("\u201c", 100, phraseY - 20 + slideUp);
        ctx.globalAlpha = alpha;

        ctx.font = 'italic 400 52px Georgia, serif';
        ctx.fillStyle = "#dfddd6";
        ctx.textAlign = "center";

        const lines = activePhrase.text.split("\n");
        lines.forEach((line, li) => {
          ctx.fillText(line, W / 2, phraseY + 40 + li * 64 + slideUp);
        });
        ctx.globalAlpha = 1;
      }

      // Bottom branding
      const footerAlpha = time < 2 ? easeOutCubic(time / 2) : time > totalDuration - 2 ? Math.max(0, (totalDuration - time) / 2) : 1;
      ctx.globalAlpha = footerAlpha * 0.5;
      ctx.font = "600 18px Inter, Arial, sans-serif";
      ctx.fillStyle = "#d9fb06";
      ctx.textAlign = "center";
      ctx.fillText("@gps1way", W / 2, H - 80);
      ctx.globalAlpha = 1;

      // Fade out at the end
      if (time > totalDuration - 2) {
        const fadeAlpha = 1 - (totalDuration - time) / 2;
        ctx.fillStyle = `rgba(26, 28, 27, ${Math.max(0, fadeAlpha)})`;
        ctx.fillRect(0, 0, W, H);
      }
    },
    [scenes, playing, recording]
  );

  // Draw initial frame
  useEffect(() => {
    drawFrame(0);
  }, [drawFrame]);

  const play = useCallback(async () => {
    if (playing || recording) return;
    if (!audioRef.current) await loadAudio();
    if (!audioRef.current) return;

    setPlaying(true);
    const audio = audioRef.current;
    audio.currentTime = 0;
    audio.play();
    startTimeRef.current = performance.now();

    const animate = (now) => {
      const elapsed = (now - startTimeRef.current) / 1000;
      const totalDuration = durationRef.current;
      if (elapsed >= totalDuration) {
        audio.pause();
        setPlaying(false);
        setProgress(0);
        drawFrame(0);
        return;
      }
      setProgress(elapsed / totalDuration);
      drawFrame(elapsed);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
  }, [playing, recording, loadAudio, drawFrame]);

  const record = useCallback(async () => {
    if (playing || recording) return;
    if (!audioRef.current) await loadAudio();
    if (!audioRef.current) return;

    setRecording(true);
    const canvas = canvasRef.current;
    const audio = audioRef.current;

    // Create audio context for mixing
    const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const source = audioCtx.createMediaElementSource(audio);
    const destination = audioCtx.createMediaStreamDestination();
    source.connect(destination);
    source.connect(audioCtx.destination);

    // Combine canvas video + audio
    const canvasStream = canvas.captureStream(30);
    const combinedStream = new MediaStream([
      ...canvasStream.getVideoTracks(),
      ...destination.stream.getAudioTracks(),
    ]);

    const chunks = [];
    const recorder = new MediaRecorder(combinedStream, {
      mimeType: "video/webm;codecs=vp9,opus",
      videoBitsPerSecond: 8000000,
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gps-testimonial-${scenes.name.toLowerCase().replace(/\s+/g, "-")}.webm`;
      a.click();
      URL.revokeObjectURL(url);
      // Reconnect audio
      source.disconnect();
      source.connect(audioCtx.destination);
      setRecording(false);
    };

    recorder.start();
    audio.currentTime = 0;
    audio.play();
    startTimeRef.current = performance.now();

    const animate = (now) => {
      const elapsed = (now - startTimeRef.current) / 1000;
      const totalDuration = durationRef.current;
      if (elapsed >= totalDuration) {
        audio.pause();
        recorder.stop();
        setProgress(0);
        drawFrame(0);
        return;
      }
      setProgress(elapsed / totalDuration);
      drawFrame(elapsed);
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
  }, [playing, recording, loadAudio, drawFrame, scenes]);

  useEffect(() => {
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <div style={{ paddingBottom: 40, borderBottom: "1px solid rgba(217,251,6,0.1)" }}>
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 13, color: "#d9fb06", letterSpacing: "0.15em", marginBottom: 8 }}>
        TESTIMONIAL {String(id).padStart(2, "0")}
      </div>
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 600, fontSize: 20, color: "#dfddd6", marginBottom: 4 }}>
        {scenes.name}
      </div>
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 14, color: "#888680", marginBottom: 16 }}>
        {scenes.role} • AI Voice: {testimonial.voice}
      </div>

      {/* Canvas preview */}
      <div style={{ width: 270, height: 480, overflow: "hidden", border: "1px solid rgba(217,251,6,0.15)" }}>
        <canvas ref={canvasRef} width={1080} height={1920} style={{ width: 270, height: 480, display: "block" }} />
      </div>

      {/* Progress */}
      <div style={{ width: 270, height: 3, background: "#302f2c", marginTop: 8 }}>
        <div style={{ width: `${progress * 100}%`, height: "100%", background: "#d9fb06", transition: "width 0.1s" }} />
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 mt-3">
        <button
          data-testid={`play-testimonial-${id}`}
          onClick={play}
          disabled={playing || recording || loading}
          style={{
            background: "transparent",
            color: "#d9fb06",
            border: "1px solid #d9fb06",
            padding: "8px 16px",
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            cursor: playing || loading ? "not-allowed" : "pointer",
            opacity: playing || recording || loading ? 0.5 : 1,
          }}
        >
          {loading ? "Loading..." : playing ? "Playing..." : audioLoaded ? "Preview" : "Load & Preview"}
        </button>
        <button
          data-testid={`record-testimonial-${id}`}
          onClick={record}
          disabled={playing || recording || loading}
          style={{
            background: "#d9fb06",
            color: "#1a1c1b",
            border: "none",
            padding: "8px 16px",
            fontFamily: "Inter, Arial, sans-serif",
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            cursor: recording || loading ? "wait" : "pointer",
            opacity: playing || recording || loading ? 0.6 : 1,
          }}
        >
          {recording ? "Recording..." : "Record & Download"}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#ff6b6b" }}>{error}</div>
      )}

      {/* Script preview */}
      <details style={{ marginTop: 16, maxWidth: 400 }}>
        <summary style={{ fontFamily: "Inter, Arial, sans-serif", fontSize: 13, color: "#888680", cursor: "pointer" }}>
          View full script
        </summary>
        <div style={{ marginTop: 8, padding: 16, background: "#302f2c", fontSize: 14, color: "#dfddd6", lineHeight: 1.7 }}>
          {testimonial.text}
        </div>
      </details>
    </div>
  );
}

export default function TestimonialReels() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/tts/testimonials`)
      .then((r) => r.json())
      .then((data) => {
        setTestimonials(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div
      data-testid="testimonial-reels-page"
      style={{ background: "#1a1c1b", minHeight: "100vh", padding: "120px 40px 80px" }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto 60px" }}>
        <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 14, color: "#888680", letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>
          AI Testimonials
        </div>
        <h1 className="brand-display" style={{ fontSize: "clamp(2.5rem, 4vw, 5rem)", marginBottom: 20 }}>
          TALKING REVIEWS
        </h1>
        <p style={{ color: "#888680", fontSize: 18, maxWidth: 700, lineHeight: 1.6 }}>
          5 AI-generated video testimonials with realistic voiceovers. Each features a unique voice,
          cinematic visuals, and key quote animations. Download as video with audio included.
        </p>
        <div style={{ marginTop: 16, padding: "12px 20px", background: "#302f2c", display: "inline-block", fontSize: 14, color: "#dfddd6" }}>
          Format: 9:16 vertical (1080×1920) • AI Voices: HD quality • Export: WebM with audio
        </div>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {loading ? (
          <div style={{ color: "#888680", fontSize: 16 }}>Loading testimonials...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 40 }}>
            {testimonials.map((t) => (
              <TestimonialReel key={t.id} id={t.id} testimonial={t} />
            ))}
          </div>
        )}

        {/* Instructions */}
        <div style={{ marginTop: 80, padding: 40, background: "#302f2c" }}>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 700, fontSize: 16, color: "#d9fb06", marginBottom: 16 }}>
            HOW IT WORKS
          </div>
          <div style={{ fontFamily: "Inter, Arial, sans-serif", fontWeight: 300, fontSize: 15, color: "#dfddd6", lineHeight: 1.8 }}>
            1. Click "Load & Preview" to hear the AI voiceover with animated visuals<br />
            2. Click "Record & Download" to export as a video file with audio<br />
            3. Transfer to your phone and post directly to Instagram Reels<br />
            4. Optionally add background music in Instagram's editor<br /><br />
            <span style={{ color: "#888680" }}>
              Each testimonial uses a different AI voice for diversity. The video includes
              the voiceover audio, animated text highlights, and GPS Method™ branding.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
